'use client';

// Page-scoped trigger for market lifecycle modals
// (spec 112-market-lifecycle-modals). Mounted inside `MarketDetailLayout`
// (which serves both /markets and /winners). Consumes already-loaded market +
// position signals, computes eligibility, and — on the first eligible & unseen
// state — marks it seen (at open, RD-5) and opens the global modal.
//
// React-Compiler safe: the previous-status ref and `Date.now()` are read only
// inside the effect, never during render (constitution §12.3).
import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import { MARKET_LIFECYCLE_MODAL_SHOWN, onEvent } from '@/events';
import { computeEligibility, type EligibilityHolding, seenKey } from '@/lib/market/lifecycle-modal-eligibility';
import { liveGraduationKey, useMarketLifecycleModalStore } from '@/stores/market-lifecycle-modal';
import { useMarketLifecycleSeenStore } from '@/stores/market-lifecycle-seen';
export interface UseLifecycleTriggerArgs {
  marketPubkey: string;
  /** Null until the market query has produced its first real status. */
  marketStatus: string | null;
  winnerOutcomeIndex: number | null;
  resolvedAtIso: string | null;
  holdings: EligibilityHolding[];
  isBacker: boolean;
  isAuthenticated: boolean;
  viewerId?: string | undefined;
  isDemoMode?: boolean | undefined;
  walletAddress: string | null | undefined;
  /** Won-variant claim affordance (from `usePrizePool`); null when N/A. */
  claim: {
    claimable: boolean;
    finalized: boolean;
    claimed: boolean;
  } | null;
  winningOutcomeLabel: string | null;
  winningOutcomeImageUrl: string | null;
  winningOutcomeImageNsfw: boolean;
}
function parseResolvedAtMs(iso: string | null): number | null {
  if (iso === null) return null;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
}
function parseHoldingsSig(sig: string): EligibilityHolding[] {
  if (sig === '') return [];
  return sig.split('|').map(pair => {
    const parts = pair.split(':');
    return {
      outcomeIndex: Number(parts[0]),
      balance: parts[1] ?? '0',
      realizedPnl: parts[2] ?? '0'
    };
  });
}
export function useMarketLifecycleModalTrigger(args) {
  const $ = _c(21);
  const {
    marketPubkey,
    marketStatus,
    winnerOutcomeIndex,
    resolvedAtIso,
    holdings,
    isBacker,
    isAuthenticated,
    viewerId,
    isDemoMode,
    walletAddress,
    claim,
    winningOutcomeLabel,
    winningOutcomeImageUrl,
    winningOutcomeImageNsfw
  } = args;
  let t0;
  if ($[0] !== holdings) {
    t0 = holdings.map(_temp).join("|");
    $[0] = holdings;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const holdingsSig = t0;
  const hasClaim = claim !== null;
  const claimClaimable = claim?.claimable ?? false;
  const claimFinalized = claim?.finalized ?? false;
  const claimClaimed = claim?.claimed ?? false;
  const prevStatusRef = useRef(null);
  const observedMarketRef = useRef(null);
  const resolutionTransitionRef = useRef(null);
  let t1;
  let t2;
  if ($[2] !== claimClaimable || $[3] !== claimClaimed || $[4] !== claimFinalized || $[5] !== hasClaim || $[6] !== holdingsSig || $[7] !== isAuthenticated || $[8] !== isBacker || $[9] !== isDemoMode || $[10] !== marketPubkey || $[11] !== marketStatus || $[12] !== resolvedAtIso || $[13] !== viewerId || $[14] !== walletAddress || $[15] !== winnerOutcomeIndex || $[16] !== winningOutcomeImageNsfw || $[17] !== winningOutcomeImageUrl || $[18] !== winningOutcomeLabel) {
    t1 = () => {
      if (marketStatus === null) {
        return;
      }
      const prevStatus = observedMarketRef.current === marketPubkey ? prevStatusRef.current : null;
      observedMarketRef.current = marketPubkey;
      prevStatusRef.current = marketStatus;
      const isResolved = marketStatus === "resolved" || marketStatus === "graduated";
      const wasResolved = prevStatus === "resolved" || prevStatus === "graduated";
      if (prevStatus !== null && !wasResolved && isResolved) {
        resolutionTransitionRef.current = {
          marketPubkey,
          walletAddress: typeof walletAddress === "string" ? walletAddress : null,
          fromStatus: prevStatus
        };
      } else {
        if (!isResolved) {
          resolutionTransitionRef.current = null;
        }
      }
      const resolutionTransition = resolutionTransitionRef.current;
      const eligibilityPrevStatus = resolutionTransition?.marketPubkey === marketPubkey && resolutionTransition.walletAddress === walletAddress ? resolutionTransition.fromStatus : prevStatus;
      const modalStore = useMarketLifecycleModalStore.getState();
      const liveKey = liveGraduationKey(isAuthenticated ? viewerId ?? walletAddress ?? "anonymous" : "anonymous", marketPubkey, isDemoMode ?? false);
      const hasLiveGraduation = modalStore.liveGraduations[liveKey] === true;
      const payload = computeEligibility({
        isAuthenticated,
        walletAddress,
        marketPubkey,
        status: marketStatus,
        prevStatus: eligibilityPrevStatus,
        winnerOutcomeIndex,
        resolvedAtMs: parseResolvedAtMs(resolvedAtIso),
        nowMs: Date.now(),
        holdings: parseHoldingsSig(holdingsSig),
        isBacker,
        claim: hasClaim ? {
          claimable: claimClaimable,
          finalized: claimFinalized,
          claimed: claimClaimed
        } : null,
        winningOutcomeLabel,
        winningOutcomeImageUrl,
        winningOutcomeImageNsfw
      });
      if (payload === null || typeof walletAddress !== "string") {
        return;
      }
      const key = seenKey(walletAddress, marketPubkey, payload.stage);
      const seenStore = useMarketLifecycleSeenStore.getState();
      if (payload.stage === "graduation" && modalStore.open && modalStore.ownerKey === liveKey && modalStore.payload?.stage === "graduation" && modalStore.payload.marketPubkey === marketPubkey) {
        if (modalStore.payload.variant === "graduation-generic" && payload.variant === "graduation-backer") {
          modalStore.refreshOpenModal(payload);
        }
        seenStore.markSeen(key);
        return;
      }
      if (payload.stage === "graduation" && hasLiveGraduation) {
        seenStore.markSeen(key);
        return;
      }
      if (seenStore.isSeen(key)) {
        if (payload.stage === "resolution") {
          modalStore.refreshOpenModal(payload);
        }
        return;
      }
      seenStore.markSeen(key);
      modalStore.openModal(payload, liveKey);
      onEvent(MARKET_LIFECYCLE_MODAL_SHOWN, {
        marketPubkey,
        stage: payload.stage,
        variant: payload.variant
      });
    };
    t2 = [marketPubkey, marketStatus, winnerOutcomeIndex, resolvedAtIso, holdingsSig, isBacker, isAuthenticated, viewerId, isDemoMode, walletAddress, hasClaim, claimClaimable, claimFinalized, claimClaimed, winningOutcomeLabel, winningOutcomeImageUrl, winningOutcomeImageNsfw];
    $[2] = claimClaimable;
    $[3] = claimClaimed;
    $[4] = claimFinalized;
    $[5] = hasClaim;
    $[6] = holdingsSig;
    $[7] = isAuthenticated;
    $[8] = isBacker;
    $[9] = isDemoMode;
    $[10] = marketPubkey;
    $[11] = marketStatus;
    $[12] = resolvedAtIso;
    $[13] = viewerId;
    $[14] = walletAddress;
    $[15] = winnerOutcomeIndex;
    $[16] = winningOutcomeImageNsfw;
    $[17] = winningOutcomeImageUrl;
    $[18] = winningOutcomeLabel;
    $[19] = t1;
    $[20] = t2;
  } else {
    t1 = $[19];
    t2 = $[20];
  }
  useEffect(t1, t2);
}
function _temp(h) {
  return `${h.outcomeIndex}:${h.balance}:${h.realizedPnl ?? "0"}`;
}